import React, { MouseEvent, FC } from 'react';
import styled, { FlattenSimpleInterpolation, css } from 'styled-components';
import _map from 'lodash/map';
import { useTranslation } from '@blocs.i18n';
import Modal from '@minecraft.modal';
import Button from '@minecraft.button';
import Loader from '@minecraft.loader';
import TalentCard from '../../../common/TalentCard';
import { TalentCount } from '../../shared/styles';

const TalentList = styled.div`
  margin-top: 1rem;
  height: calc(100vh - 18.75rem);
  overflow: auto;
`;

const StyledTalentCardWrapper = styled.button`
  &,
  &:active,
  &:focus,
  &:hover {
    outline: none;
    border: 0;
    background: none;
    padding: 0;
    width: 100%;
  }
`;

const StyledTalentCard = styled(TalentCard)<{ $isActive: boolean }>`
  ${(props): FlattenSimpleInterpolation =>
    props.$isActive &&
    css`
      & > div {
        border: 0.125rem solid ${props.theme.colors.primary.primary};
      }
    `}
`;

const StyledCloseButton = styled(Button)`
  padding: 0 1rem;
  width: 100%;
  margin-top: 1rem;
  font-size: 1rem;
  height: 2.5rem;
`;

const LoaderWrapper = styled.div`
  position: relative;
  min-height: 10rem;
`;

type Props = {
  isNextPageLoading: boolean;
  hasNextPage: boolean;
  currentTalentGuid: string;
  talentCount: number;
  talentList: any[];
  handleToggleClick: () => void;
  handleTalentChange: (event: MouseEvent<HTMLButtonElement>) => void;
  handleScroll: (event: MouseEvent<HTMLDivElement>) => void;
};

const SeeAllTalentModal: FC<Props> = ({
  isNextPageLoading,
  hasNextPage,
  currentTalentGuid,
  talentCount,
  talentList,
  handleToggleClick,
  handleTalentChange,
  handleScroll,
}) => {
  const { t } = useTranslation();

  return (
    <Modal isFooterHidden title={t('common:seeAllTalentModal.title')} handleToggleClick={handleToggleClick}>
      <TalentCount>{t('common:count.talent', { count: talentCount })}</TalentCount>
      <TalentList onScroll={handleScroll}>
        {_map(talentList, (talent) => (
          <StyledTalentCardWrapper key={talent.id} value={talent.guid} onClick={handleTalentChange}>
            <StyledTalentCard
              $isActive={talent.guid === currentTalentGuid}
              skipRepsShortCodes={false}
              submission={{
                profile: {
                  ...talent,
                  profileId: talent.id,
                },
                contactInfo: talent?.contactInfo || talent?.profile?.contactInfo,
              }}
            />
          </StyledTalentCardWrapper>
        ))}
        {hasNextPage && <LoaderWrapper>{isNextPageLoading && <Loader />}</LoaderWrapper>}
      </TalentList>
      <StyledCloseButton outlined onClick={handleToggleClick}>
        {t('common:button.close')}
      </StyledCloseButton>
    </Modal>
  );
};

export default SeeAllTalentModal;
